import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import useSEO from "../lib/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact",
    description:
      "Contactez l'équipe EventMarket Pro. Support 7j/7, réponse sous 24h ouvrées. Email, téléphone, formulaire — choisissez le canal qui vous convient.",
  });

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.message.trim().length < 10) {
      setError("Le message doit contenir au moins 10 caractères.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé. Nous revenons vers vous sous 24h.");
      setForm({ name: "", email: "", subject: "", message: "", company: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "L'envoi a échoué. Réessayez ou écrivez-nous directement à hello@eventmarketpro.fr.";
      setError(typeof msg === "string" ? msg : "L'envoi a échoué.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-cream min-h-[calc(100vh-80px)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-5 w-5 text-accent" />
              <p className="text-label text-muted-foreground">Nous écrire</p>
            </div>
            <h1
              data-testid="contact-title"
              className="font-heading font-light tracking-editorial text-4xl sm:text-5xl text-ink mb-6 leading-[0.95]"
            >
              Parlons de votre<br />
              <span className="font-display italic text-accent">événement.</span>
            </h1>
            <p className="text-ink/70 font-light leading-relaxed mb-10">
              Une question, un partenariat, un retour ? L&apos;équipe EventMarket Pro répond
              sous 24h ouvrées. Vous pouvez aussi consulter notre{" "}
              <Link to="/aide" className="text-accent underline hover:text-ink">Centre d&apos;aide</Link>.
            </p>

            <div className="space-y-5 mb-10">
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-label text-ink/50 mb-1">Email</p>
                  <a href="mailto:hello@eventmarketpro.fr" className="text-sm text-ink hover:text-accent">
                    hello@eventmarketpro.fr
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-label text-ink/50 mb-1">Téléphone</p>
                  <a href="tel:+33665028700" className="text-sm text-ink hover:text-accent">
                    06 65 02 87 00
                  </a>
                  <p className="text-xs text-ink/50 mt-0.5">Lun-Ven, 9h-18h</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-label text-ink/50 mb-1">Siège</p>
                  <p className="text-sm text-ink">SAVEURS NORMANDES<br />4 rue de la Laiterie, 27400 Louviers</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-ink/10 p-8 lg:p-10 rounded-md shadow-sm">
              {sent ? (
                <div data-testid="contact-success" className="flex flex-col items-center text-center py-10">
                  <CheckCircle2 className="h-12 w-12 text-accent mb-4" />
                  <h2 className="font-heading text-3xl text-ink font-light tracking-editorial mb-3">
                    Merci !
                  </h2>
                  <p className="text-ink/70 max-w-md font-light leading-relaxed mb-6">
                    Votre message est bien arrivé. Nous revenons vers vous sous 24h ouvrées
                    à l&apos;adresse indiquée.
                  </p>
                  <Button
                    data-testid="contact-send-another"
                    onClick={() => setSent(false)}
                    variant="outline"
                    className="rounded-none border-ink text-ink hover:bg-ink hover:text-cream"
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="contact-name" className="text-xs uppercase tracking-wider text-ink/60 mb-1.5 block">
                        Nom complet *
                      </Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-name"
                        required
                        minLength={2}
                        maxLength={120}
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Marie Dupont"
                        className="rounded-none border-ink/20 focus:border-ink"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="text-xs uppercase tracking-wider text-ink/60 mb-1.5 block">
                        Email *
                      </Label>
                      <Input
                        id="contact-email"
                        data-testid="contact-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="marie@exemple.fr"
                        className="rounded-none border-ink/20 focus:border-ink"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-subject" className="text-xs uppercase tracking-wider text-ink/60 mb-1.5 block">
                      Sujet *
                    </Label>
                    <Input
                      id="contact-subject"
                      data-testid="contact-subject"
                      required
                      minLength={2}
                      maxLength={160}
                      value={form.subject}
                      onChange={update("subject")}
                      placeholder="Demande de partenariat, support technique, etc."
                      className="rounded-none border-ink/20 focus:border-ink"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact-message" className="text-xs uppercase tracking-wider text-ink/60 mb-1.5 block">
                      Message *
                    </Label>
                    <Textarea
                      id="contact-message"
                      data-testid="contact-message"
                      required
                      maxLength={4000}
                      rows={6}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Décrivez votre demande en quelques lignes…"
                      className="rounded-none border-ink/20 focus:border-ink resize-none"
                    />
                    <p className="text-[11px] text-ink/40 mt-1.5">
                      {form.message.length}/4000 caractères
                    </p>
                  </div>

                  {/* Honeypot — hidden from humans, bots fill it */}
                  <div className="hidden" aria-hidden="true">
                    <Label htmlFor="contact-company">Société (ne pas remplir)</Label>
                    <input
                      id="contact-company"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.company}
                      onChange={update("company")}
                    />
                  </div>

                  {error && (
                    <div data-testid="contact-error" className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 rounded text-sm text-red-700">
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <p className="text-[11px] text-ink/50 leading-relaxed">
                    En envoyant ce formulaire, vous acceptez que vos données soient utilisées
                    pour traiter votre demande. Voir notre{" "}
                    <Link to="/legal/confidentialite" className="text-accent underline">
                      Politique de confidentialité
                    </Link>.
                  </p>

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={sending}
                    className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none py-6 text-sm font-semibold tracking-wide"
                  >
                    {sending ? "Envoi…" : (
                      <>
                        <Send className="h-4 w-4 mr-2" /> Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
